import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { db } from '../services/firebase'
import { collection, query, orderBy, limit, getDocs, deleteDoc, doc, getCountFromServer, where, onSnapshot, setDoc } from 'firebase/firestore'
import { useAuth } from '../context/AuthContext'
import './AdminPage.css'
import BillingWidget from '../components/Admin/BillingWidget'
import RegressionDashboard from '../components/Admin/RegressionDashboard'

function AdminPage() {
    const { userProfile } = useAuth()
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalIdentifications: 0,
        completedIdentifications: 0,
        totalCatalogs: 0,
        totalMarks: 0,
        activeMarketplaces: 0
    })
    const [indexStatus, setIndexStatus] = useState({
        loading: true,
        status: 'unknown',
        processed: 0,
        total: 0,
        missingEmbedding: 0,
        verified: false,
        error: null
    })
    const [loading, setLoading] = useState(true)

    // Fetch Admin Stats (Users, Idents, etc.)
    useEffect(() => {
        const fetchAdminData = async () => {
            if (userProfile?.role !== 'admin') return

            setLoading(true)
            try {
                // Optimized fetching
                const [
                    usersCount,
                    identsCount,
                    marksCount,
                    activeMarketsCount,
                    completedIdentsCount,
                    catalogSourcesSnap
                ] = await Promise.all([
                    getCountFromServer(collection(db, 'users')),
                    getCountFromServer(collection(db, 'identifications')),
                    getCountFromServer(collection(db, 'marks')),
                    getCountFromServer(query(collection(db, 'marketplaceSources'), where('enabled', '==', true))),
                    getCountFromServer(query(collection(db, 'identifications'), where('status', 'in', ['completed', 'approved']))),
                    getDocs(collection(db, 'catalogSources')) // Fetch full docs to sum marksCount
                ])

                const totalScraped = catalogSourcesSnap.docs.reduce((acc, doc) => acc + (doc.data().marksCount || 0), 0);

                setStats({
                    totalUsers: usersCount.data().count,
                    totalIdentifications: identsCount.data().count,
                    completedIdentifications: completedIdentsCount.data().count,
                    totalMarks: marksCount.data().count,
                    activeMarketplaces: activeMarketsCount.data().count,
                    totalScrapedEvents: totalScraped // New stat
                })

            } catch (err) {
                console.error('Error fetching admin data:', err)
            } finally {
                setLoading(false)
            }
        }

        fetchAdminData()
    }, [userProfile])

    // Listen to Index Status (Real-time)
    useEffect(() => {
        if (userProfile?.role !== 'admin') return;

        const unsub = onSnapshot(doc(db, 'config', 'reindexing'), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setIndexStatus(prev => ({
                    ...prev,
                    loading: false,
                    status: data.status || 'ready',
                    processed: data.processed || 0,
                    total: data.total || stats.totalMarks || 0,
                    error: data.status === 'error' ? data.lastError : null
                }));
            } else {
                setIndexStatus(prev => ({ ...prev, loading: false, status: 'ready', total: stats.totalMarks || 0 }));
            }
        });
        return () => unsub();
    }, [userProfile, stats.totalMarks]);

    // NEW: Listen to Audit Status
    const [auditStatus, setAuditStatus] = useState({
        status: 'unknown',
        processed: 0,
        corrupted: 0,
        total: 0
    });

    useEffect(() => {
        if (userProfile?.role !== 'admin') return;
        const unsub = onSnapshot(doc(db, 'config', 'audit'), (docSnap) => {
            if (docSnap.exists()) {
                setAuditStatus(docSnap.data());
            }
        });
        return () => unsub();
    }, [userProfile]);

    // NEW: Global Debug Mode Listener
    const [debugEnabled, setDebugEnabled] = useState(false);
    useEffect(() => {
        if (userProfile?.role !== 'admin') return;
        const unsub = onSnapshot(doc(db, 'config', 'system'), (docSnap) => {
            if (docSnap.exists()) {
                setDebugEnabled(docSnap.data().debug || false);
            }
        });
        return () => unsub();
    }, [userProfile]);

    const toggleDebugMode = async () => {
        const newState = !debugEnabled;
        try {
            // Create doc if not exists
            await setDoc(doc(db, 'config', 'system'), { debug: newState }, { merge: true });
        } catch (err) {
            alert("Failed to toggle debug mode: " + err.message);
        }
    };

    const handleStartAudit = async () => {
        if (!confirm("Start Deep Background Audit? This analyzes content of 20,000 images.")) return;
        try {
            await fetch('https://europe-west1-porcelain-identification-78c76.cloudfunctions.net/startAudit?repair-secret=porcelain-repair-2024', { method: 'POST' });
        } catch (e) { alert("Failed to start: " + e.message); }
    };

    const handlePurgeCorrupted = async () => {
        if (!confirm(`PERMANENTLY DELETE ${auditStatus.corrupted} corrupted images?`)) return;
        try {
            const res = await fetch('https://europe-west1-porcelain-identification-78c76.cloudfunctions.net/purgeCorrupted?repair-secret=porcelain-repair-2024', { method: 'POST' });
            const data = await res.json();
            alert(`Purged ${data.deleted} images.`);
        } catch (e) { alert("Failed to purge: " + e.message); }
    };

    const handleVerifyIndex = async (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent card click
        if (!confirm("Run heavy verification scan? This counts all missing embeddings.")) return;

        setIndexStatus(prev => ({ ...prev, loading: true }));
        try {
            // Call HTTP function directly or via SDK
            const response = await fetch('https://europe-west1-porcelain-identification-78c76.cloudfunctions.net/checkIndexingStatus');
            const result = await response.json();

            setIndexStatus(prev => ({
                ...prev,
                loading: false,
                verified: true,
                missingEmbedding: result.missingEmbedding,
                total: result.scanned || result.total // Use scanned count from server
            }));
            // Alert removed; UI will update via getIndexStatusText
        } catch (err) {
            console.error("Verification failed", err);
            alert("Verification failed: " + err.message);
            setIndexStatus(prev => ({ ...prev, loading: false }));
        }
    };

    // Helper to determine index health color
    const getIndexHealthColor = () => {
        if (indexStatus.loading) return '#888';
        if (indexStatus.error) return '#f44336';
        if (indexStatus.status === 'running') return '#2196F3'; // Blue
        if (indexStatus.missingEmbedding > 0) return '#FF9800'; // Orange
        return '#4CAF50'; // Green
    }

    const getIndexStatusText = () => {
        if (indexStatus.loading) return 'Loading...';
        if (indexStatus.error) return 'Error';
        if (indexStatus.status === 'running') {
            const pct = Math.round((indexStatus.processed / (indexStatus.total || 1)) * 100);
            return `Processing ${pct}% (${indexStatus.processed}/${indexStatus.total})`;
        }
        if (indexStatus.verified) {
            return (
                <div style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                    ✅ <strong>Verified {indexStatus.total?.toLocaleString()} items</strong><br />
                    {indexStatus.missingEmbedding === 0
                        ? <span style={{ color: '#4CAF50' }}>All Clear (100%)</span>
                        : <span style={{ color: '#f44336' }}>{indexStatus.missingEmbedding} Missing Embeddings</span>
                    }
                </div>
            );
        }
        return 'Ready to Verify';
    }


    if (userProfile?.role !== 'admin') {
        return (
            <div className="admin-denied">
                <div className="container">
                    <h1>Access Denied</h1>
                    <p>You do not have permission to view this page.</p>
                </div>
            </div>
        )
    }

    if (loading) {
        return (
            <div className="admin-page loading">
                <div className="loading-spinner large" />
                <p>Loading Dashboard...</p>
            </div>
        )
    }

    return (
        <div className="admin-page">
            <div className="container">
                <div className="page-header">
                    <div className="header-content">
                        <h1>Admin Dashboard</h1>
                        <p>System overview and management</p>
                    </div>
                </div>


                {/* Stats Grid - Consolidated Navigation */}
                <div className="stats-grid">
                    <Link to="/admin/users" className="stat-card card-glass clickable">
                        <span className="stat-icon">👥</span>
                        <div className="stat-info">
                            <h3>User Management</h3>
                            <div className="stat-value">{stats.totalUsers}</div>
                            <span className="stat-subtext">Registered Users</span>
                        </div>
                    </Link>

                    {/* Merged Hallmark Database Card */}
                    <Link to="/admin/marks" className="stat-card card-glass clickable">
                        <span className="stat-icon">🏷️</span>
                        <div className="stat-info">
                            <h3>Hallmark Database</h3>
                            {indexStatus.status === 'running' ? (
                                <div className="stat-value" style={{ fontSize: '1.2rem', color: getIndexHealthColor() }}>
                                    {getIndexStatusText()}
                                </div>
                            ) : (
                                <div className="stat-value">
                                    {stats.totalMarks || indexStatus.total}
                                    <span style={{ fontSize: '0.9rem', color: '#888', display: 'block', marginTop: '5px' }}>
                                        (of {stats.totalScrapedEvents?.toLocaleString() || '?'} scraped)
                                    </span>
                                </div>
                            )}
                            <span className="stat-subtext">
                                {indexStatus.status === 'running' ? 'Re-indexing in progress...' : 'Unique Marks / Total Events'}
                            </span>
                        </div>
                    </Link>

                    <Link to="/admin/regression" className="stat-card card-glass clickable">
                        <span className="stat-icon">🧪</span>
                        <div className="stat-info">
                            <h3>Regression Tool</h3>
                            <div className="stat-value">Test Set</div>
                            <span className="stat-subtext">Label Ground Truth</span>
                        </div>
                    </Link>

                    <Link to="/admin/config" className="stat-card card-glass clickable">
                        <span className="stat-icon">⚙️</span>
                        <div className="stat-info">
                            <h3>System Config</h3>
                            <div className="stat-value">{stats.activeMarketplaces}</div>
                            <span className="stat-subtext">Active Marketplaces</span>
                        </div>
                    </Link>

                    <div className="stat-card card-glass">
                        <span className="stat-icon">🔍</span>
                        <div className="stat-info">
                            <h3>Identifications</h3>
                            <div className="stat-value">{stats.totalIdentifications}</div>
                            <span className="stat-subtext">{stats.completedIdentifications} Completed</span>
                        </div>
                    </div>

                    {/* NEW: Billing Widget */}
                    <BillingWidget />
                </div>



                <div className="admin-content-grid single-column">
                    {/* System Status */}
                    <section className="admin-section card-glass system-status-section">
                        <h2>System Status</h2>
                        <div className="health-list">
                            <div className="health-item">
                                <span className="health-label">Cloud Vision API</span>
                                <span className="health-status status-ok">Stable</span>
                            </div>
                            <div className="health-item">
                                <span className="health-label">Marketplace Connectors</span>
                                <span className="health-status status-ok">{stats.activeMarketplaces || 0} Active</span>
                            </div>
                            <div className="health-item">
                                <span className="health-label">Firestore Database</span>
                                <span className="health-status status-ok">Online</span>
                            </div>

                            {/* Global Debug Toggle */}
                            <div className="health-item" style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #ddd' }}>
                                <span className="health-label">Global Debug Mode</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '40px', height: '20px' }}>
                                        <input
                                            type="checkbox"
                                            checked={debugEnabled}
                                            onChange={toggleDebugMode}
                                            style={{ opacity: 0, width: 0, height: 0 }}
                                        />
                                        <span style={{
                                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                            backgroundColor: debugEnabled ? '#2196F3' : '#ccc',
                                            transition: '.4s', borderRadius: '20px'
                                        }}>
                                            <span style={{
                                                position: 'absolute', content: "", height: '16px', width: '16px', left: '2px', bottom: '2px',
                                                backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                                                transform: debugEnabled ? 'translateX(20px)' : 'translateX(0)'
                                            }} />
                                        </span>
                                    </label>
                                    <span style={{ fontSize: '0.8rem', color: debugEnabled ? '#2196F3' : '#666' }}>
                                        {debugEnabled ? 'Enabled (Verbose Logs)' : 'Disabled'}
                                    </span>
                                </div>
                            </div>
                            <div className="health-item">
                                <span className="health-label">Search Index vector store</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span className={`health-status ${indexStatus.missingEmbedding > 0 ? 'status-warning' : 'status-ok'}`}>
                                        {indexStatus.verified
                                            ? (indexStatus.missingEmbedding > 0 ? `${indexStatus.missingEmbedding} Issues` : 'Verified')
                                            : (indexStatus.status === 'running' ? 'Indexing...' : 'Ready')}
                                    </span>
                                    <button
                                        onClick={handleVerifyIndex}
                                        disabled={indexStatus.loading}
                                        style={{
                                            background: 'none',
                                            border: '1px solid #8D6E63',
                                            color: '#3E2723',
                                            cursor: 'pointer',
                                            padding: '2px 8px',
                                            fontSize: '0.75rem',
                                            borderRadius: '4px',
                                            opacity: indexStatus.loading ? 0.5 : 1
                                        }}
                                        title="Run deep consistency check"
                                    >
                                        {indexStatus.loading ? '...' : 'Verify'}
                                    </button>
                                </div>
                            </div>

                            {/* NEW: Audit Section */}
                            <div className="health-item" style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #ddd' }}>
                                <span className="health-label">Deep Content Audit (Magic Bytes)</span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>

                                    {/* Status Bar */}
                                    {auditStatus.status === 'running' && (
                                        <div style={{ width: '100%', background: '#eee', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                                            <div style={{
                                                width: `${Math.min(((auditStatus.processed || 0) / (auditStatus.total || 1)) * 100, 100)}%`,
                                                background: '#2196F3',
                                                height: '100%'
                                            }} />
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                                        <span>
                                            {auditStatus.status === 'running'
                                                ? `Scanning... ${auditStatus.processed} / ${auditStatus.total}`
                                                : (auditStatus.processed > 0 ? `Last Scan: ${auditStatus.processed} items` : 'Not run yet')
                                            }
                                        </span>
                                        <span style={{ color: (auditStatus.corrupted || 0) > 0 ? '#f44336' : '#4CAF50', fontWeight: 'bold' }}>
                                            {(auditStatus.corrupted || 0)} Corrupted Found
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                        <button
                                            onClick={handleStartAudit}
                                            disabled={auditStatus.status === 'running'}
                                            style={{
                                                background: '#fff',
                                                border: '1px solid #aaa',
                                                color: '#333',
                                                cursor: 'pointer',
                                                padding: '4px 8px',
                                                fontSize: '0.75rem',
                                                borderRadius: '4px',
                                                opacity: auditStatus.status === 'running' ? 0.5 : 1
                                            }}
                                        >
                                            {auditStatus.status === 'running' ? 'Running in Background...' : 'Start Deep Audit'}
                                        </button>

                                        <button
                                            onClick={handlePurgeCorrupted}
                                            disabled={auditStatus.status === 'running' || !auditStatus.corrupted}
                                            style={{
                                                background: '#ffeeee',
                                                border: '1px solid #d32f2f',
                                                color: '#d32f2f',
                                                cursor: 'pointer',
                                                padding: '4px 8px',
                                                fontSize: '0.75rem',
                                                borderRadius: '4px',
                                                fontWeight: 'bold',
                                                opacity: (auditStatus.status === 'running' || !auditStatus.corrupted) ? 0.5 : 1
                                            }}
                                        >
                                            Purge {(auditStatus.corrupted || 0)} Corrupted
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* NEW: Regression Dashboard */}
                    <section className="admin-section card-glass" style={{ width: '100%', maxWidth: '800px', boxSizing: 'border-box' }}>
                        <RegressionDashboard />
                    </section>
                </div>

            </div>
        </div>
    )
}

export default AdminPage
